import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Container, Dialog,
  DialogContent, DialogTitle, Divider, IconButton, MenuItem,
  Paper, Select, Stack, Typography,
} from "@mui/material";
import { MdClose, MdContentCopy, MdExpandLess, MdExpandMore, MdCheckCircle } from "react-icons/md";
import { db, auth } from "../../firebase/firebase-config";
import Colors from "../../assets/styles/Colors";
import { ADMIN_API_URL } from "../variables/globals";

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_META = {
  pending:        { label: "Pendiente",       color: Colors.textSecondary },
  finished:       { label: "Completado",      color: Colors.success       },
  partially_paid: { label: "Pago parcial",    color: Colors.warning       },
  failed:         { label: "Fallido",         color: Colors.error         },
  expired:        { label: "Expirado",        color: Colors.error         },
  waiting:        { label: "Esperando pago",  color: Colors.info          },
  confirming:     { label: "Confirmando",     color: Colors.info          },
  sending:        { label: "Enviando",        color: Colors.info          },
};

const PLAN_LABELS = {
  access: "ACCESS", pro: "PRO", elite: "ELITE",
  luxe: "LUXE", socio: "SOCIO", basic: "BASIC",
};

const TYPE_LABELS = {
  activation: "Activación", renewal: "Renovación", upgrade: "Upgrade",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(ts, withTime = false) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  if (withTime) return d.toLocaleString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function StatusChip({ status }) {
  const meta = STATUS_META[status] || { label: status, color: Colors.textMuted };
  return (
    <Chip
      label={meta.label}
      size="small"
      sx={{ backgroundColor: meta.color + "22", color: meta.color, border: `1px solid ${meta.color}44`, fontWeight: 700, fontSize: 11 }}
    />
  );
}

function CopyBtn({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <IconButton size="small" onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      sx={{ color: copied ? Colors.success : Colors.textMuted, p: 0.3 }}>
      <MdContentCopy size={13} />
    </IconButton>
  );
}

function Field({ label, value, mono = false, copy = false }) {
  if (!value && value !== 0) return null;
  return (
    <Box>
      <Typography variant="caption" sx={{ color: Colors.textSecondary, display: "block", mb: 0.3, letterSpacing: "0.5px", textTransform: "uppercase", fontSize: 10 }}>{label}</Typography>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="body2" sx={{ color: Colors.textPrimary, fontFamily: mono ? "monospace" : undefined, wordBreak: "break-all", fontSize: 13 }}>{value}</Typography>
        {copy && <CopyBtn value={String(value)} />}
      </Stack>
    </Box>
  );
}

function Section({ title, children }) {
  return (
    <Box>
      <Typography variant="overline" sx={{ color: Colors.textSecondary, letterSpacing: 2, fontSize: 10, display: "block", mb: 1.5 }}>{title}</Typography>
      <Stack spacing={1.5}>{children}</Stack>
    </Box>
  );
}

function JsonBlock({ data, label }) {
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <Button
        size="small"
        onClick={() => setOpen(v => !v)}
        endIcon={open ? <MdExpandLess /> : <MdExpandMore />}
        sx={{ color: Colors.textSecondary, textTransform: "none", fontSize: 12, px: 0 }}
      >
        {label}
      </Button>
      {open && (
        <Box sx={{ mt: 1, p: 1.5, borderRadius: 1.5, backgroundColor: "#0a0a0f", border: `1px solid ${Colors.borderSecondary}`, overflow: "auto", maxHeight: 320 }}>
          <Typography component="pre" variant="caption" sx={{ color: "#a78bfa", fontFamily: "monospace", fontSize: 11, whiteSpace: "pre-wrap", wordBreak: "break-all", margin: 0 }}>
            {JSON.stringify(data, null, 2)}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ── Detail dialog ─────────────────────────────────────────────────────────────

function OrderDetailDialog({ order, onClose, onActivated }) {
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState("");

  if (!order) return null;

  async function handleActivate() {
    setActivating(true);
    setActivateError("");
    try {
      const token = await auth.currentUser.getIdToken(true);
      const res = await fetch(`${ADMIN_API_URL}/activateOrder`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ orderId: order.orderId, force: order.status !== "finished" }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Error al activar");
      onActivated && onActivated();
      onClose();
    } catch (err) {
      setActivateError(err.message);
    } finally {
      setActivating(false);
    }
  }

  const events = Array.isArray(order.webhookEvents) ? [...order.webhookEvents].reverse() : [];
  const latestEvent = events[0] || null;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}`, borderRadius: 3 } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", pb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ color: Colors.textPrimary, fontWeight: 700 }}>Detalle de Orden</Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <Typography variant="caption" sx={{ color: Colors.textMuted, fontFamily: "monospace" }}>{order.orderId}</Typography>
            <CopyBtn value={order.orderId} />
            <StatusChip status={order.status} />
          </Stack>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: Colors.textSecondary }}><MdClose size={20} /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Stack spacing={3}>

          {/* Order info */}
          <Section title="Información de la orden">
            <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
              <Stack spacing={1.5} flex={1}>
                <Field label="Email" value={order.email} copy />
                <Field label="Plan" value={PLAN_LABELS[order.targetPlanKey] || order.targetPlanKey} />
                <Field label="Tipo" value={TYPE_LABELS[order.productType] || order.productType} />
                <Field label="Monto" value={order.amount ? `$${order.amount} USD` : null} />
              </Stack>
              <Stack spacing={1.5} flex={1}>
                <Field label="Fecha de creación" value={fmtDate(order.createdAt, true)} />
                <Field label="Fecha de completado" value={fmtDate(order.completedAt, true)} />
                <Field label="Plan activo hasta" value={fmtDate(order.nextDueAt, true)} />
                <Field label="UID de usuario" value={order.uid} mono copy />
              </Stack>
            </Stack>
          </Section>

          <Divider sx={{ borderColor: Colors.borderSecondary }} />

          {/* NowPayments data */}
          <Section title="NowPayments">
            <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
              <Stack spacing={1.5} flex={1}>
                <Field label="Invoice ID" value={order.nowpaymentsInvoiceId} mono copy />
                <Field label="Payment ID" value={order.nowpaymentsPaymentId} mono copy />
                <Field label="IPN callbacks recibidos" value={order.ipnCallbackCount ?? 0} />
              </Stack>
              <Stack spacing={1.5} flex={1}>
                {latestEvent && (
                  <>
                    <Field label="Estado de pago (último IPN)" value={latestEvent.payment_status} />
                    <Field label="Moneda de pago" value={latestEvent.pay_currency?.toUpperCase()} />
                    <Field label="Monto esperado" value={latestEvent.pay_amount ? `${latestEvent.pay_amount} ${latestEvent.pay_currency?.toUpperCase()}` : null} />
                    <Field label="Monto recibido" value={latestEvent.actually_paid ? `${latestEvent.actually_paid} ${latestEvent.pay_currency?.toUpperCase()}` : null} />
                    <Field label="Hash de transacción" value={latestEvent.payin_hash} mono copy />
                    <Field label="Dirección de pago" value={latestEvent.pay_address} mono copy />
                  </>
                )}
              </Stack>
            </Stack>

            {events.length > 0 && (
              <Stack spacing={1.5}>
                {events.map((evt, i) => (
                  <JsonBlock key={i} data={evt} label={`IPN #${events.length - i} — ${evt.payment_status || "?"} · ${fmtDate({ toDate: () => new Date(evt.updated_at || Date.now()) }, true)}`} />
                ))}
              </Stack>
            )}
          </Section>

          <Divider sx={{ borderColor: Colors.borderSecondary }} />

          {/* Referral */}
          <Section title="Referido">
            {order.referredBy ? (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                <Stack spacing={1.5} flex={1}>
                  <Field label="Código de referido (L1)" value={order.referredBy} mono copy />
                  <Field label="Comisión generada" value={order.commissionCreated ? "Sí" : "No"} />
                </Stack>
              </Stack>
            ) : (
              <Typography variant="body2" sx={{ color: Colors.textMuted }}>Sin referido en esta orden.</Typography>
            )}
          </Section>

          <Divider sx={{ borderColor: Colors.borderSecondary }} />

          {/* Source & metadata */}
          <Section title="Metadata">
            <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
              <Stack spacing={1.5} flex={1}>
                <Field label="Fuente" value={order.source} />
                <Field label="Producto" value={order.productKey} mono />
                <Field label="Ciclo de facturación" value={order.billingCycle} />
              </Stack>
              <Stack spacing={1.5} flex={1}>
                <Field label="Email enviado" value={order.emailSent ? "Sí" : "No"} />
                <Field label="Activado por admin" value={order.adminActivated ? `Sí — ${order.activatedBy ?? ""}` : "No"} />
                <Field label="Fecha de activación" value={fmtDate(order.activatedAt, true)} />
                <Field label="Notas de admin" value={order.adminNotes} />
              </Stack>
            </Stack>
          </Section>

          {/* Activate action */}
          {!order.adminActivated && (
            <>
              <Divider sx={{ borderColor: Colors.borderSecondary }} />
              <Box>
                <Typography variant="overline" sx={{ color: Colors.textSecondary, letterSpacing: 2, fontSize: 10, display: "block", mb: 1.5 }}>ACCIÓN</Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button
                    variant="contained"
                    startIcon={<MdCheckCircle />}
                    onClick={handleActivate}
                    disabled={activating}
                    sx={{ backgroundColor: Colors.success, color: "#fff", fontWeight: 700, "&:hover": { backgroundColor: Colors.success + "cc" } }}
                  >
                    {activating ? "Activando…" : "Activar cuenta"}
                  </Button>
                  <Typography variant="caption" sx={{ color: Colors.textSecondary }}>
                    Inicia el plan desde hoy · el contador de 30 días comienza al activar.
                  </Typography>
                </Stack>
                {activateError && (
                  <Typography variant="caption" sx={{ color: Colors.error, display: "block", mt: 1 }}>{activateError}</Typography>
                )}
              </Box>
            </>
          )}

          {order.adminActivated && (
            <>
              <Divider sx={{ borderColor: Colors.borderSecondary }} />
              <Stack direction="row" spacing={1} alignItems="center">
                <MdCheckCircle color={Colors.success} size={18} />
                <Typography variant="body2" sx={{ color: Colors.success, fontWeight: 600 }}>
                  Cuenta activada el {fmtDate(order.activatedAt, true)} por {order.activatedBy ?? "admin"}
                </Typography>
              </Stack>
            </>
          )}

        </Stack>
      </DialogContent>
    </Dialog>
  );
}

// ── Main view ─────────────────────────────────────────────────────────────────

export default function OrdersView() {
  const [orders, setOrders]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [selected, setSelected]   = useState(null);
  const [statusFilter, setStatusFilter] = useState("Todos");
  const [planFilter, setPlanFilter]     = useState("Todos");
  const [search, setSearch]             = useState("");

  useEffect(() => {
    const unsub = db.collection("orders").orderBy("createdAt", "desc").onSnapshot(
      snap => { setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); },
      () => { setError("No se pudieron cargar las órdenes."); setLoading(false); },
    );
    return unsub;
  }, []);

  const filtered = useMemo(() => orders.filter(o => {
    if (statusFilter !== "Todos" && o.status !== statusFilter) return false;
    if (planFilter   !== "Todos" && o.targetPlanKey !== planFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (!o.email?.toLowerCase().includes(q) && !o.orderId?.toLowerCase().includes(q) && !o.referredBy?.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [orders, statusFilter, planFilter, search]);

  const totals = useMemo(() => ({
    total:    orders.length,
    finished: orders.filter(o => o.status === "finished").length,
    pending:  orders.filter(o => o.status === "pending").length,
    revenue:  orders.filter(o => o.status === "finished").reduce((s, o) => s + (o.amount ?? 0), 0),
  }), [orders]);

  const selectSx = { minWidth: 160, backgroundColor: Colors.bgSecondary, color: Colors.textPrimary, ".MuiOutlinedInput-notchedOutline": { borderColor: Colors.borderSecondary } };

  return (
    <Box sx={{ width: "100%", py: 3 }}>
      <Container maxWidth="xl">

        {/* Header */}
        <Paper sx={{ mb: 3, p: 2.5, borderRadius: 3, background: `linear-gradient(135deg, ${Colors.bgSecondary} 0%, ${Colors.bgTertiary} 100%)`, border: `1px solid ${Colors.borderSecondary}` }}>
          <Typography variant="h5" sx={{ color: Colors.textPrimary, fontWeight: 700 }}>Órdenes</Typography>
          <Typography variant="body2" sx={{ color: Colors.textSecondary, mt: 0.5 }}>
            Historial completo de pagos con datos de NowPayments. Colección: <code>orders</code>
          </Typography>
        </Paper>

        {/* Summary cards */}
        {!loading && !error && (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
            {[
              { label: "Total órdenes",   value: totals.total,              color: Colors.textPrimary  },
              { label: "Completadas",     value: totals.finished,           color: Colors.success      },
              { label: "Pendientes",      value: totals.pending,            color: Colors.warning      },
              { label: "Revenue total",   value: `$${totals.revenue} USD`,  color: Colors.info         },
            ].map(card => (
              <Paper key={card.label} sx={{ flex: 1, p: 2, borderRadius: 2, backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}` }}>
                <Typography variant="caption" sx={{ color: Colors.textSecondary }}>{card.label}</Typography>
                <Typography variant="h6" sx={{ color: card.color, mt: 0.5, fontWeight: 700 }}>{card.value}</Typography>
              </Paper>
            ))}
          </Stack>
        )}

        {/* Filters */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
          <Box component="input"
            placeholder="Buscar email, orden, referido…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ px: 1.5, py: 1, borderRadius: 1, border: `1px solid ${Colors.borderSecondary}`, backgroundColor: Colors.bgSecondary, color: Colors.textPrimary, fontSize: 14, outline: "none", minWidth: 260 }}
          />
          <Select size="small" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} sx={selectSx}>
            <MenuItem value="Todos">Todos los estados</MenuItem>
            {Object.entries(STATUS_META).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </Select>
          <Select size="small" value={planFilter} onChange={e => setPlanFilter(e.target.value)} sx={selectSx}>
            <MenuItem value="Todos">Todos los planes</MenuItem>
            {Object.entries(PLAN_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
          </Select>
        </Stack>

        {loading && <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>}
        {!loading && error && <Typography color="error">{error}</Typography>}
        {!loading && !error && !filtered.length && (
          <Typography variant="body2" sx={{ color: Colors.textSecondary }}>Sin resultados.</Typography>
        )}

        {!loading && !error && filtered.length > 0 && (
          <Paper sx={{ borderRadius: 3, backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}`, overflow: "hidden" }}>

            {/* Header row */}
            <Box sx={{ px: 3, py: 1.5, backgroundColor: Colors.bgTertiary, borderBottom: `1px solid ${Colors.borderSecondary}` }}>
              <Stack direction="row" spacing={1}>
                {["Fecha", "Email", "Plan", "Tipo", "Monto", "Referido", "NP Invoice", "Estado"].map(h => (
                  <Box key={h} sx={{ flex: h === "Email" || h === "NP Invoice" ? 2 : 1 }}>
                    <Typography variant="subtitle2" sx={{ color: Colors.textSecondary, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>{h}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>

            {filtered.map(o => (
              <Box
                key={o.id}
                onClick={() => setSelected(o)}
                sx={{ px: 3, py: 1.75, borderBottom: `1px solid ${Colors.borderSecondary}`, cursor: "pointer", transition: "background-color 0.15s", "&:hover": { backgroundColor: Colors.bgTertiary } }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: Colors.textSecondary }}>{fmtDate(o.createdAt)}</Typography>
                  </Box>
                  <Box sx={{ flex: 2, overflow: "hidden" }}>
                    <Typography variant="caption" sx={{ color: Colors.textPrimary, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.email ?? "—"}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: Colors.textPrimary, fontWeight: 600 }}>{PLAN_LABELS[o.targetPlanKey] ?? "—"}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: Colors.textSecondary }}>{TYPE_LABELS[o.productType] ?? o.productType ?? "—"}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: Colors.textPrimary }}>{o.amount ? `$${o.amount}` : "—"}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    {o.referredBy
                      ? <Chip label={o.referredBy} size="small" sx={{ backgroundColor: Colors.info + "22", color: Colors.info, border: `1px solid ${Colors.info}44`, fontSize: 10, fontWeight: 700 }} />
                      : <Typography variant="caption" sx={{ color: Colors.textMuted }}>—</Typography>
                    }
                  </Box>
                  <Box sx={{ flex: 2, overflow: "hidden" }}>
                    <Typography variant="caption" sx={{ color: Colors.textMuted, fontFamily: "monospace", fontSize: 10, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
                      {o.nowpaymentsInvoiceId ?? "—"}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StatusChip status={o.status} />
                  </Box>
                </Stack>
              </Box>
            ))}
          </Paper>
        )}
      </Container>

      {selected && (
        <OrderDetailDialog
          order={selected}
          onClose={() => setSelected(null)}
          onActivated={() => setSelected(null)}
        />
      )}
    </Box>
  );
}
